import React, { useEffect, useState } from "react";
import { client } from "../services/api";
import { ObjectId } from 'bson';
import { toast } from "react-toastify";
import Modal from "./Modal";
import { LinearProgress, Box, Typography } from "@mui/material";
import { structureTypes, structuralIssues } from "../constants/formOptions";

function ModalAddStrucure({ isOpen, onClose, onDone, projectId, structure, objective }) {
  const [name, setName] = useState("");
  const [type, setType] = useState("");
  const [recognizedStructuralIssue, setRecognizedStructuralIssue] = useState("");
  const [issues, setIssues] = useState([]);
  const [dataFile, setDataFile] = useState({});
  const [existingDataFile, setExistingDataFile] = useState(null);
  const [existingImageIssueFile, setExistingImageIssueFile] = useState(null);
  const [imageIssueFile, setImageIssueFile] = useState(null);
  const [description, setDescription] = useState("");
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadProgressDescription, setUploadProgressDescription] = useState("");
  const [isUploading, setIsUploading] = useState(false);

  const handleClose = () => {
    if (isUploading) return;
    if (dataFile._id) {
      if (window.confirm("The structure file uploaded will be deleted, continue?")) {
        client.deleteGeometry(projectId, dataFile._id)
          .then(() => toast.success("Structure file deleted successfully!"));
      } else {
        return;
      }
    }
    resetForm();
    onClose();
  };

  const handleDone = () => {
    if (!name.trim() || !type) {
      toast.error("Please provide both name and type for the structure.", {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }
    if (isUploading) return;
    onDone({ _id: new ObjectId(), name, type, issues, dataFile, imageIssueFile, description, objective });
    resetForm();
    onClose();
  };

  const handleCheckboxChange = (value) => {
    setIssues((prevIssues) =>
      prevIssues.includes(value)
        ? prevIssues.filter((issue) => issue !== value)
        : [...prevIssues, value]
    );
  };

  useEffect(() => {
    if (structure) {
      setType(structure.type);
      setIssues(structure.issues);
      structure.dataFile?._id && client.getDataFile(structure.dataFile._id).then(result => {
        if (result.statusText === 'OK') {
          setExistingDataFile(result.data);
        }
      });
      structure.imageIssueFile && client.getUpload(structure.imageIssueFile).then(result => {
        if (result.statusText === 'OK') {
          setExistingImageIssueFile(result.data);
        }
      });
    }
  }, [structure]);

  const resetForm = () => {
    setName("");
    setType("");
    setIssues([]);
    setDataFile({});
    setImageIssueFile(null);
    setDescription("");
    setUploadProgress(0);
    setUploadProgressDescription("");
    setIsUploading(false);
  };

  const handleFileUpload = async (file, setFileState) => {
    if (!file) {
      toast.error("Please select a file to upload.");
      return;
    }
    if (!projectId) {
      toast.error("Please select a project to upload.");
      return;
    }

    const MAX_FILE_SIZE = 5 * 1024 * 1024;
    if (file.size > MAX_FILE_SIZE) {
      toast.error("File size exceeds 5MB. Please upload a smaller file.", {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    const fileName = file.name.replace(/\.[^/.]+$/, "").replace(/_/g, " ");

    setIsUploading(true);
    setUploadProgress(0);
    setUploadProgressDescription("");

    try {
      let currentStep = 0;
      let totalSteps = 1;
      client.uploadGeometry(file, fileName, projectId, (progress) => {
        if (progress.status === "started") {
          totalSteps = progress.steps;
        } else if (progress.status === "progress") {
          currentStep++;
          setUploadProgress((currentStep / totalSteps) * 100);
          setUploadProgressDescription(progress.description);
        } else if (progress.status === "done") {
          setFileState({
            _id: progress.result.datafile_id,
            layer: {
              extent: progress.result.total_bounds_wkt,
              source_id: progress.result.tileset_id,
              source_layer: progress.result.source_layer,
            },
          });
          toast.success("File uploaded successfully!");
          setUploadProgress(100);
          setUploadProgressDescription("");
          setIsUploading(false);
        } else if (progress.status === "error") {
          toast.error("File upload error: " + progress.message);
          setUploadProgress(0);
          setUploadProgressDescription("");
          setIsUploading(false);
        }
      });
    } catch (error) {
      console.error("File upload error:", error);
      toast.error("File upload failed.");
      setIsUploading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} className="large">
      <div className="modal-add-structure">
        <h6>{!!structure ? "Infrastructure" : "Add an infrastructure"}</h6>
        {!!structure ? "Type of infrastructure" : "Select the type of infrastructure *"}
        <div className="radio-group">
          {structureTypes.map((option) => (
            <label key={option.value}>
              <input
                type="radio"
                name="structure_type"
                value={option.value}
                checked={type === option.value}
                onChange={(e) => setType(e.target.value)}
                disabled={!!structure}
              />
              {option.label}
            </label>
          ))}
        </div>
        Name of infrastructure *<br />
        <input type="text" defaultValue={structure?.name} onChange={(e) => setName(e.target.value)} disabled={!!structure} />
        <br />
        Description<br />
        <textarea
          placeholder="less than 100 words"
          className="description"
          onChange={(e) => setDescription(e.target.value)}
          style={{ width: '100%' }}
          defaultValue={structure?.description}
          disabled={!!structure}
        /><br/>
        {!!structure ? (
          <>
            Structure file: {existingDataFile?.upload && <a href={`${process.env.REACT_APP_URL_API}/upload/${existingDataFile?.upload._id}/download`} target="_blank" rel="noreferrer">{existingDataFile?.upload.filename}</a>}
          </>
        ) : (
          <>
            Infrastructure location (.geojson, .kml, .kmz, Zipped Shapefile .zip):<br />
            <input
              type="file"
              onChange={(e) => handleFileUpload(e.target.files[0], setDataFile)}
              accept=".geojson,.GEOJSON,.kml,.KML,.kmz,.KMZ,.zip,.ZIP"
            />
            {isUploading && (
              <Box sx={{ width: "100%", mt: 2 }}>
                <LinearProgress variant="determinate" value={uploadProgress} />
                <Typography variant="body2" color="text.secondary" align="center">
                  {`${Math.round(uploadProgress)}%`} {uploadProgressDescription && `- ${uploadProgressDescription}`}
                </Typography>
              </Box>
            )}
          </>
        )}
        <br />
        {objective==="evaluate" && !structure && (
          <p>
            Is there already a recognized structural issue? *
            <input
              type="radio"
              name="recognizedStructuralIssue"
              value="yes"
              checked={recognizedStructuralIssue === "yes"}
              onChange={() => setRecognizedStructuralIssue("yes")}
            /> Yes
            <input
              type="radio"
              name="existingData"
              value="no"
              checked={recognizedStructuralIssue === "no"}
              onChange={() => setRecognizedStructuralIssue("no")}
            /> No
          </p>
        )}
        {recognizedStructuralIssue === "yes" && (
          <>
            Select the type of structural issue *
            <div className="radio-group">
              {structuralIssues.map((option) => (
                <label key={option.value}>
                  <input
                    type="checkbox"
                    name="structural_issues[]"
                    value={option.value}
                    checked={issues.includes(option.value)}
                    onChange={() => handleCheckboxChange(option.value)}
                    disabled={!!structure}
                  />
                  {option.label}
                </label>
              ))}
            </div>
            {!!structure ? (
              <>
                Photographic surveys {existingImageIssueFile && <a href={`${process.env.REACT_APP_URL_API}/upload/${existingImageIssueFile._id}/download`} target="_blank" rel="noreferrer">{existingImageIssueFile.filename}</a>}
              </>
            ) : (
              <>
                Photographic surveys<br />
                <input
                  type="file"
                  className="image-issue-file"
                  style={{ width: "auto", height: "auto" }}
                  onChange={(e) => setImageIssueFile(e.target.files[0])}
                />
              </>
            )}
          </>
        )}
      </div>

      <div className="modal-actions" style={{ justifyContent: "flex-end" }}>
      {!!structure ? (
          <>
            <button className="button-outline outline" onClick={handleClose}>
              Close
            </button>
          </>
        ) : (
          <>
            <button className="button-outline outline" onClick={handleClose} disabled={isUploading}>
              Cancel
            </button>
            <button className="button-dark" onClick={handleDone} disabled={isUploading}>
              Done
            </button>
          </>
        )}
      </div>
    </Modal>
  );
}

export default ModalAddStrucure;
